import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { mapSelectors, layerSelectors } from '@opengeoweb/store';
import type { CoreAppStore } from '@opengeoweb/store';
import { FIVE_MIN_MS } from '../utils/timeUtils';

function getMaxTimeFromDimension(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dimension: any,
): string | null {
  if (!dimension) {
    return null;
  }
  // Prefer maxValue if available
  if (dimension.maxValue) {
    return dimension.maxValue as string;
  }
  // Fall back to last entry in comma-separated values
  if (dimension.values) {
    const parts = (dimension.values as string).split(',');
    const last = parts[parts.length - 1]?.trim();
    if (last) {
      return last;
    }
  }
  return null;
}

export function useAutoUpdateAnimationWindow(
  mapId: string | null,
  layerId: string | null,
  handleAnimationWindowChange: (
    window: [Date, Date] | null,
    disableAutoUpdate: boolean,
  ) => void,
  forceRefetch: () => void,
  enabled = true,
): void {
  // Read the current animation end time from Redux
  const animationEndTime = useSelector((state: CoreAppStore) =>
    mapId ? mapSelectors.getAnimationEndTime(state, mapId) : null,
  );

  // Read the current animation start time from Redux
  const animationStartTime = useSelector((state: CoreAppStore) =>
    mapId ? mapSelectors.getAnimationStartTime(state, mapId) : null,
  );

  // Read the layer's time dimension
  const layerMaxTime = useSelector((state: CoreAppStore) => {
    if (!layerId) {
      return null;
    }
    const dim = layerSelectors.getLayerTimeDimension(state, layerId);
    return getMaxTimeFromDimension(dim);
  });

  // Helper to apply the update
  const applyUpdate = useCallback(
    (force = true) => {
      if (!layerMaxTime) {
        return;
      }

      // Only update if different from current
      if (
        animationStartTime &&
        animationEndTime &&
        new Date(animationEndTime).valueOf() !==
          new Date(layerMaxTime).valueOf()
      ) {
        const newAnimationStartTime =
          new Date(layerMaxTime).valueOf() -
          (new Date(animationEndTime).valueOf() -
            new Date(animationStartTime).valueOf());
        if (
          new Date(animationEndTime).valueOf() <
            new Date(layerMaxTime).valueOf() &&
          !force
        ) {
          handleAnimationWindowChange(null, true);
          forceRefetch();
        } else {
          handleAnimationWindowChange(
            [new Date(newAnimationStartTime), new Date(layerMaxTime)],
            false,
          );
          forceRefetch();
        }
      }
    },
    [
      animationEndTime,
      layerMaxTime,
      animationStartTime,
      handleAnimationWindowChange,
      forceRefetch,
    ],
  );

  // Apply on mount / when enabled changes
  useEffect(() => {
    if (enabled) {
      applyUpdate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, layerMaxTime]);
  useEffect(() => {
    if (enabled) {
      applyUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationEndTime, animationStartTime]);

  // Poll every 5 minutes
  useEffect(() => {
    if (!enabled || !mapId || !layerId) {
      return undefined;
    }
    const id = setInterval(applyUpdate, FIVE_MIN_MS);
    return (): void => clearInterval(id);
  }, [mapId, layerId, applyUpdate, enabled]);
}
